import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { CheckCircle2, Mail, Phone, StickyNote, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/activities/')({
  component: ActivitiesIndexPage,
})

type ActivityType = 'call' | 'meeting' | 'email' | 'task' | 'note'
type Priority = 'low' | 'normal' | 'high'
type RelatedToType = 'lead' | 'account' | 'contact' | 'opportunity'

type ActivityRow = {
  id: string
  type?: ActivityType | null
  subject?: string | null
  body?: string | null
  dueAt?: string | Date | null
  completedAt?: string | Date | null
  priority?: Priority | null
  relatedToType?: RelatedToType | null
  relatedToId?: string | null
  ownerUserId?: string | null
  outcome?: string | null
}

const TAB_KEYS = ['open', 'overdue', 'completed'] as const
type TabKey = (typeof TAB_KEYS)[number]

function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleString()
  } catch {
    return '-'
  }
}

function typeIcon(activityType: ActivityType | null | undefined) {
  switch (activityType) {
    case 'call':
      return <Phone className="h-4 w-4" />
    case 'email':
      return <Mail className="h-4 w-4" />
    case 'meeting':
      return <Users className="h-4 w-4" />
    case 'note':
      return <StickyNote className="h-4 w-4" />
    default:
      return <CheckCircle2 className="h-4 w-4" />
  }
}

function ActivitiesIndexPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<TabKey>('open')

  const typeLabel = (typeKey: ActivityType | null | undefined): string => {
    switch (typeKey) {
      case 'call':
        return t('crm.activity.typeCall')
      case 'meeting':
        return t('crm.activity.typeMeeting')
      case 'email':
        return t('crm.activity.typeEmail')
      case 'task':
        return t('crm.activity.typeTask')
      case 'note':
        return t('crm.activity.typeNote')
      default:
        return '-'
    }
  }

  const priorityLabel = (p: Priority | null | undefined): string => {
    switch (p) {
      case 'low':
        return t('crm.activity.priorityLow')
      case 'high':
        return t('crm.activity.priorityHigh')
      default:
        return t('crm.activity.priorityNormal')
    }
  }

  // "Open" and "Completed" both filter completedAt client-side — the list
  // procedure doesn't expose a completedAt filter, only onlyMine / onlyOverdue.
  const listInput = useMemo(() => {
    if (tab === 'overdue') return { limit: 500, offset: 0, onlyMine: true, onlyOverdue: true }
    return { limit: 500, offset: 0, onlyMine: true }
  }, [tab])

  const { data, isLoading } = useQuery(orpc.crm.activities.list.queryOptions({ input: listInput }))
  const allRows = (data as ActivityRow[] | undefined) ?? []

  const rows = useMemo(() => {
    if (tab === 'completed') return allRows.filter((r) => r.completedAt != null)
    if (tab === 'open') return allRows.filter((r) => r.completedAt == null)
    return allRows
  }, [allRows, tab])

  const completeMutation = useMutation(
    orpc.crm.activities.complete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.crm.activities.list.key() })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ActivityRow | null>(null)

  const openCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }
  const openEdit = (row: ActivityRow) => {
    setEditing(row)
    setDialogOpen(true)
  }

  const columns: EntityListColumn<ActivityRow>[] = [
    {
      key: 'done',
      header: '',
      render: (r) => (
        // Render a button that toggles completion directly — the row's
        // onClick opens the edit modal, so we use stopPropagation to
        // keep the complete action from re-opening it.
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          aria-label={t('crm.activity.markComplete')}
          aria-pressed={r.completedAt != null}
          onClick={(e) => {
            e.stopPropagation()
            if (r.completedAt == null) completeMutation.mutate({ id: r.id })
          }}
        >
          {r.completedAt != null ? (
            <CheckCircle2 className="h-4 w-4 text-primary" />
          ) : (
            <span className="h-4 w-4 rounded border border-input" aria-hidden />
          )}
        </Button>
      ),
      className: 'w-10',
    },
    {
      key: 'type',
      header: t('crm.activity.type'),
      render: (r) => (
        <span className="flex items-center gap-2">
          {typeIcon(r.type)}
          {typeLabel(r.type)}
        </span>
      ),
    },
    {
      key: 'subject',
      header: t('crm.activity.subject'),
      render: (r) => <span className="font-medium">{r.subject ?? '-'}</span>,
    },
    {
      key: 'relatedTo',
      header: t('crm.activity.relatedTo'),
      render: (r) => {
        if (!r.relatedToType || !r.relatedToId) return '-'
        const relatedId = r.relatedToId
        const go = () => {
          switch (r.relatedToType) {
            case 'lead':
              navigate({ to: '/crm/leads/$leadId', params: { leadId: relatedId } })
              return
            case 'account':
              navigate({
                to: '/crm/accounts/$accountId',
                params: { accountId: relatedId },
              })
              return
            case 'contact':
              navigate({
                to: '/crm/contacts/$contactId',
                params: { contactId: relatedId },
              })
              return
            case 'opportunity':
              navigate({
                to: '/crm/opportunities/$opportunityId',
                params: { opportunityId: relatedId },
              })
              return
          }
        }
        return (
          <Button
            variant="link"
            className="h-auto p-0 capitalize"
            onClick={(e) => {
              e.stopPropagation()
              go()
            }}
          >
            {r.relatedToType}: {relatedId.slice(0, 8)}
          </Button>
        )
      },
    },
    { key: 'dueAt', header: t('crm.activity.dueAt'), render: (r) => formatDate(r.dueAt) },
    {
      key: 'priority',
      header: t('crm.activity.priority'),
      render: (r) => <span>{priorityLabel(r.priority)}</span>,
    },
    {
      key: 'completedAt',
      header: t('crm.activity.completedAt'),
      render: (r) =>
        r.completedAt ? (
          formatDate(r.completedAt)
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('crm.activity.entityPlural')}</h1>
        <Button onClick={openCreate}>{t('crm.activity.newActivity')}</Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="open">{t('crm.activity.myOpen')}</TabsTrigger>
          <TabsTrigger value="overdue">{t('crm.activity.overdue')}</TabsTrigger>
          <TabsTrigger value="completed">{t('crm.activity.completed')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <EntityList
        rows={rows}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(r) => openEdit(r)}
      />

      <ActivityDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={() => {
          setDialogOpen(false)
          queryClient.invalidateQueries({ queryKey: orpc.crm.activities.list.key() })
        }}
      />
    </div>
  )
}

interface ActivityDialogProps {
  open: boolean
  onOpenChange: (v: boolean) => void
  editing: ActivityRow | null
  onSaved: () => void
}

const TYPES: ActivityType[] = ['call', 'meeting', 'email', 'task', 'note']
const PRIORITIES: Priority[] = ['low', 'normal', 'high']
const RELATED_TYPES: RelatedToType[] = ['lead', 'account', 'contact', 'opportunity']

function ActivityDialog({ open, onOpenChange, editing, onSaved }: ActivityDialogProps) {
  const t = useTranslations()
  const isEdit = editing != null
  const [type, setType] = useState<ActivityType>('task')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [relatedToType, setRelatedToType] = useState<RelatedToType>('lead')
  const [relatedToId, setRelatedToId] = useState('')
  const [dueAt, setDueAt] = useState('')
  const [priority, setPriority] = useState<Priority>('normal')
  const [outcome, setOutcome] = useState('')

  // Seed fields when an existing row comes in; reset when the dialog opens
  // for create. useState initializers don't re-run, so we reconcile here.
  useMemo(() => {
    if (!open) return
    if (editing) {
      setType((editing.type ?? 'task') as ActivityType)
      setSubject(editing.subject ?? '')
      setBody(editing.body ?? '')
      setRelatedToType((editing.relatedToType ?? 'lead') as RelatedToType)
      setRelatedToId(editing.relatedToId ?? '')
      setDueAt(editing.dueAt ? new Date(editing.dueAt).toISOString().slice(0, 16) : '')
      setPriority((editing.priority ?? 'normal') as Priority)
      setOutcome(editing.outcome ?? '')
    } else {
      setType('task')
      setSubject('')
      setBody('')
      setRelatedToType('lead')
      setRelatedToId('')
      setDueAt('')
      setPriority('normal')
      setOutcome('')
    }
  }, [open, editing])

  const createMutation = useMutation(
    orpc.crm.activities.create.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.activity.entity'))
        onSaved()
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const updateMutation = useMutation(
    orpc.crm.activities.update.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.save'))
        onSaved()
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const canSubmit = subject.trim() !== '' && relatedToId.trim() !== ''

  const submit = () => {
    if (!canSubmit) return
    const payload = {
      type,
      subject,
      body: body || undefined,
      relatedToType,
      relatedToId,
      dueAt: dueAt ? new Date(dueAt) : undefined,
      priority,
    }
    if (isEdit && editing) {
      updateMutation.mutate({ id: editing.id, ...payload } as Parameters<
        typeof updateMutation.mutate
      >[0])
    } else {
      createMutation.mutate(payload as Parameters<typeof createMutation.mutate>[0])
    }
  }

  const pending = createMutation.isPending || updateMutation.isPending

  const typeLabelFor = (typeKey: ActivityType): string => {
    switch (typeKey) {
      case 'call':
        return t('crm.activity.typeCall')
      case 'meeting':
        return t('crm.activity.typeMeeting')
      case 'email':
        return t('crm.activity.typeEmail')
      case 'task':
        return t('crm.activity.typeTask')
      case 'note':
        return t('crm.activity.typeNote')
    }
  }
  const priorityLabelFor = (p: Priority): string => {
    switch (p) {
      case 'low':
        return t('crm.activity.priorityLow')
      case 'high':
        return t('crm.activity.priorityHigh')
      default:
        return t('crm.activity.priorityNormal')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `${t('crm.common.edit')} ${t('crm.activity.entity').toLowerCase()}`
              : t('crm.activity.newActivity')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t('crm.activity.type')}</Label>
              <Select value={type} onValueChange={(v) => setType(v as ActivityType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((typeKey) => (
                    <SelectItem key={typeKey} value={typeKey}>
                      {typeLabelFor(typeKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t('crm.activity.priority')}</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {priorityLabelFor(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="activity-subject">{t('crm.activity.subject')}</Label>
            <Input
              id="activity-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="activity-body">{t('crm.activity.body')}</Label>
            <Textarea
              id="activity-body"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t('crm.activity.relatedTo')}</Label>
              <Select
                value={relatedToType}
                onValueChange={(v) => setRelatedToType(v as RelatedToType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RELATED_TYPES.map((rt) => (
                    <SelectItem key={rt} value={rt} className="capitalize">
                      {rt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="activity-related-id">{t('crm.activity.relatedTo')} ID</Label>
              <Input
                id="activity-related-id"
                value={relatedToId}
                onChange={(e) => setRelatedToId(e.target.value)}
                placeholder="UUID"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="activity-due">{t('crm.activity.dueAt')}</Label>
            <Input
              id="activity-due"
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
          </div>

          {isEdit && editing?.completedAt && (
            <div className="space-y-1">
              <Label htmlFor="activity-outcome">{t('crm.activity.outcome')}</Label>
              <Input
                id="activity-outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('crm.common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit || pending}>
            {pending
              ? t('crm.common.saving')
              : isEdit
                ? t('crm.common.save')
                : t('crm.common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
